import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import {
    Buildings,
    PersonVideo,
    GeoAlt,
    Telephone,
    Search,
} from "react-bootstrap-icons";
import Form from "../../Components/Form/form";
import { useClientForm } from "../../Hook/Client/useClientForm";
import { applyCnpjMask, applyCpfMask, applyCepMask } from "../../Helpers/Masc";
import { useCepLookup } from "../../Hook/Client/useCepLookup";
import { useCnpjLookup } from "../../Hook/Client/useCnpjLookup";
import { useAlert } from "../../Provider/Alert/AlertContext";
import { validateCpfCnpj } from "../../Helpers/Form";

export default function ClientForm({ id = null }) {
    const { t } = useTranslation();
    const { addAlert } = useAlert();
    const navigate = useNavigate();

    const {
        isEditing,
        formData,
        setFormData,
        states,
        getListCities,
        cities,
        saveClient,
        setAddressFromCep,
        setClientFormCnpj,
    } = useClientForm({
        id,
    });

    const { lookup: lookupCep, loading: loadingCep } = useCepLookup({
        onSuccess: (data) => {
            setAddressFromCep(data);
        },
        onError: (errMsg) => {
            console.error("Erro ao buscar CEP:", errMsg);
            addAlert("danger", t("client.form.error.zip_code.title"), errMsg);
        },
    });

    const { lookup: lookupCnpj } = useCnpjLookup({
        onSuccess: (data) => {
            setClientFormCnpj(data);
        },
        onError: (errMsg) => {
            console.error("Erro ao buscar CNPJ:", errMsg);
            addAlert("danger", t("client.form.error.cnpj.title"), errMsg);
        },
    });

    const formConfig = {
        isEditing,
        submitText: isEditing
            ? t("common.actions.update")
            : t("common.actions.save"),
        sections: [
            {
                title: t("client.form.sections.basic-info"),
                icon: formData?.legal_entity ? Buildings : PersonVideo,
                columns: 2,
                fields: [
                    {
                        name: "name",
                        label: formData?.legal_entity
                            ? t("client.form.field.razao")
                            : t("client.form.field.name"),
                        type: "text",
                        placeholder: formData?.legal_entity
                            ? t("client.form.placeholders.razao")
                            : t("client.form.placeholders.name"),
                        required: true,
                        errorMessages: {
                            required: t("client.form.error.name.required"),
                        },
                    },
                    {
                        name: "last_name",
                        label: formData?.legal_entity
                            ? t("client.form.field.fantasy")
                            : t("client.form.field.surname"),
                        type: "text",
                        placeholder: formData?.legal_entity
                            ? t("client.form.placeholders.fantasy")
                            : t("client.form.placeholders.surname"),
                    },
                    {
                        name: "legal_entity",
                        label: t("client.form.field.legal_entity"),
                        type: "radio",
                        options: [
                            {
                                value: "true",
                                label: t("client.form.field.pj"),
                            },
                            {
                                value: "false",
                                label: t("client.form.field.pf"),
                            },
                        ],
                        radioOrientation: "horizontal",
                    },
                    {
                        name: "cpf_cnpj",
                        label: formData?.legal_entity
                            ? t("client.form.field.pj")
                            : t("client.form.field.pf"),
                        type: "text",
                        placeholder: formData?.legal_entity
                            ? t("client.form.placeholders.pj")
                            : t("client.form.placeholders.pf"),
                        required: true,
                        mask: (value) => {
                            return formData.legal_entity
                                ? applyCnpjMask(value)
                                : applyCpfMask(value);
                        },
                        actionButton: formData?.legal_entity
                            ? {
                                  icon: Search,
                                  onClick: (value) => {
                                      lookupCnpj(String(value || ""));
                                  },
                                  tooltip: t("tooltips.lookup_cnpj"),
                              }
                            : null,
                        validate: (value) => {
                            if (
                                !validateCpfCnpj(value, formData.legal_entity)
                            ) {
                                if (formData.legal_entity) {
                                    return t("client.form.error.cnpj.invalid");
                                } else {
                                    return t("client.form.error.cpf.invalid");
                                }
                            }
                        },
                    },
                    {
                        name: "is_active",
                        label: t("client.form.field.is_active"),
                        type: "checkbox",
                    },
                    {
                        name: "ie",
                        label: t("client.form.field.ie"),
                        type: "text",
                        placeholder: t("client.form.placeholders.ie"),
                        required: false,
                        hide: !formData.legal_entity,
                    },
                ],
            },
            {
                title: t("client.form.sections.address-info"),
                icon: GeoAlt,
                columns: 2,
                fields: [
                    {
                        name: "address.zip_code",
                        label: t("client.form.field.zip_code"),
                        type: "text",
                        placeholder: t("client.form.placeholders.zip_code"),
                        mask: (value) => applyCepMask(value),
                        actionButton: {
                            icon: Search,
                            onClick: (value) => {
                                lookupCep(String(value || ""));
                            },
                            tooltip: t("tooltips.lookup_cep"),
                            loading: loadingCep,
                        },
                    },
                    {
                        name: "address.state.id",
                        label: t("client.form.field.state"),
                        type: "select",
                        options: [
                            {
                                label: t("client.form.placeholders.state"),
                                value: "",
                            },
                            ...states.map((state) => ({
                                label: state.name,
                                value: state.id,
                            })),
                        ],
                        onChange: (value) => {
                            const stateId = value ? parseInt(value, 10) : null;
                            setFormData((prevData) => ({
                                ...prevData,
                                address: {
                                    ...prevData.address,
                                    state: { id: stateId },
                                    city: { id: null },
                                },
                            }));
                            if (stateId) {
                                // Fetch cities for the selected state
                                getListCities(stateId);
                            }
                        },
                    },
                    {
                        name: "address.city.id",
                        label: t("client.form.field.city"),
                        type: "select",
                        options: [
                            {
                                label: t("client.form.placeholders.city"),
                                value: "",
                            },
                            ...cities.map((city) => ({
                                label: city.name,
                                value: city.id,
                            })),
                        ],
                        onChange: (value) => {
                            const cityId = value ? parseInt(value, 10) : null;
                            setFormData((prevData) => ({
                                ...prevData,
                                address: {
                                    ...prevData.address,
                                    city: { id: cityId },
                                },
                            }));
                        },
                    },
                    {
                        name: "address.street",
                        label: t("client.form.field.street"),
                        type: "text",
                        placeholder: t("client.form.placeholders.street"),
                    },
                    {
                        name: "address.number",
                        label: t("client.form.field.number"),
                        type: "text",
                        placeholder: t("client.form.placeholders.number"),
                    },
                    {
                        name: "address.complement",
                        label: t("client.form.field.complement"),
                        type: "text",
                        placeholder: t("client.form.placeholders.complement"),
                    },
                    {
                        name: "address.neighborhood",
                        label: t("client.form.field.neighborhood"),
                        type: "text",
                        placeholder: t("client.form.placeholders.neighborhood"),
                    },
                ],
            },
            {
                title: t("client.form.sections.contact-info"),
                icon: Telephone,
                columns: 2,
                fields: [
                    {
                        name: "contact.phone",
                        label: t("client.form.field.phone"),
                        type: "text",
                        placeholder: t("client.form.placeholders.phone"),
                    },
                    {
                        name: "contact.mobile",
                        label: t("client.form.field.mobile"),
                        type: "text",
                        placeholder: t("client.form.placeholders.mobile"),
                    },
                    {
                        name: "contact.email",
                        label: t("client.form.field.email"),
                        type: "email",
                        placeholder: t("client.form.placeholders.email"),
                    },
                    {
                        name: "contact.other",
                        label: t("client.form.field.other"),
                        type: "text",
                        placeholder: t("client.form.placeholders.other"),
                    },
                ],
            },
        ],

        onSubmit: async () => {
            saveClient();
        },
        onCancel: () => {
            navigate("/client");
        },
        onChange: (newFormData) => {
            setFormData(newFormData);
        },
        data: formData,
    };

    return <Form {...formConfig} />;
}
